import { useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api_deleteSession } from "@/lib/api";

export default function DeleteSessionButton({ sessionId, onDeleted, size = "md" }) {
  const [deleting, setDeleting] = useState(false);
  const iconSize = size === "sm" ? "w-3.5 h-3.5" : "w-4 h-4";
  const btnPad = size === "sm" ? "p-1.5" : "p-2";

  const handleDelete = async (e) => {
    e?.stopPropagation?.();
    setDeleting(true);
    try {
      await api_deleteSession(sessionId);
      toast.success("Session deleted.");
      onDeleted?.();
    } catch {
      toast.error("Could not delete session.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          data-testid={`delete-session-${sessionId}`}
          className={`${btnPad} rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors`}
          title="Delete session"
          aria-label="Delete session"
        >
          <Trash2 className={iconSize} />
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent onClick={(e) => e.stopPropagation()}>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this session?</AlertDialogTitle>
          <AlertDialogDescription>
            Every shot, reflection, and note from this session will be permanently removed — including its contribution to your stats and streak. You can't undo this.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`delete-session-cancel-${sessionId}`} onClick={(e) => e.stopPropagation()}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid={`delete-session-confirm-${sessionId}`}
            disabled={deleting}
            onClick={handleDelete}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {deleting ? "Deleting…" : "Delete forever"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
